/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';

import RouterService from '@ember/routing/router-service';
import AccountModel from 'cio/models/account';
import CrumbsService from 'cio/services/crumbs';
import AnalyticsService from 'cio/services/analytics';
import { EventsType } from 'cio/utils/analytics/events';
import BillingService from 'cio/services/billing';
import SidebarService, { StaticSidebarId } from 'cio/services/sidebar';
import HydraService from 'cio/services/hydra';
import { Result } from 'cio/services/docs-search';
import FeaturesService from 'ember-feature-flags/services/features';
import AccountService from 'cio/services/account';
import { scheduleOnce } from '@ember/runloop';

enum HelpType {
  Support = 'support',
  Feedback = 'feedback',
}

export enum MixpanelEvents {
  HelpCenterOpened = 'Help Menu Opened',
  AskCommunityClicked = 'Ask the Community',
  PlatformDocsClicked = 'Platform Documentation',
  ApiDocsClicked = 'Api Documentation',
  ReleaseNotesClicked = 'Release Notes',
  FeedbackRequestStarted = 'Send Us Feedback',
  HelpRequestStarted = 'Get Help With an Issue',
  SystemStatusClicked = 'System Status',
  GrantSupportAccessClicked = 'Set Support Access',
  HelpCenterSearched = 'Help Center Searched',
}

interface HelpDropdownArgs {
  account: AccountModel;
  queryParamShowSupport: boolean;
  onCancelSupport: () => unknown;
}

export default class HelpDropdown extends Component<HelpDropdownArgs> {
  @service analytics!: AnalyticsService;
  @service crumbs!: CrumbsService;
  @service router!: RouterService;
  @service billing!: BillingService;
  @service sidebar!: SidebarService;
  @service hydra!: HydraService;
  @service features!: FeaturesService;
  @service account!: AccountService;

  @tracked helpType?: HelpType;
  @tracked shouldDisplaySupport = false;
  @tracked hasUserInput = false;
  @tracked isCancelModalVisible = false;

  mixpanelEvents = MixpanelEvents;
  readonly sidebarId = StaticSidebarId.HelpDropdown;

  hasTrackedDocsSearchInput = false;

  constructor(owner: unknown, args: HelpDropdownArgs) {
    super(owner, args);
    this.checkQueryParam();
  }

  @computed('args.account.supportTeamAccessEnabled')
  get supportTeamAccessEnabled() {
    return this.args.account.supportTeamAccessEnabled;
  }

  checkQueryParam() {
    let { supportTier } = this.account.current;
    if (this.args.queryParamShowSupport && (supportTier === 'email' || supportTier === 'chat')) {
      this.helpType = HelpType.Support;
      if (this.helpType === 'support') {
        scheduleOnce('afterRender', this, this.toggleSidebar);
        this.handleSupportOpen();
      }
    }
    if (supportTier === 'community') {
      scheduleOnce('afterRender', this, this.args.onCancelSupport);
    }
  }

  @action
  closeHelpModal() {
    this.helpType = undefined;
    this.args.onCancelSupport();
  }

  @action
  setHelpType(helpType: HelpType) {
    this.helpType = helpType;
    if (helpType === HelpType.Support) {
      this.trackClickEvent(MixpanelEvents.HelpRequestStarted);
    } else {
      this.trackClickEvent(MixpanelEvents.FeedbackRequestStarted);
    }
  }

  @action
  handleContextualHelpItemClick(item: Result) {
    this.trackEvent('contextual_help_item_clicked', {
      item_title: item.title,
      item_href: item.href,
    });
  }

  @action
  toggleSidebar() {
    this.sidebar.toggle(this.sidebarId);

    if (this.sidebar.activeSidebarId === this.sidebarId) {
      this.trackClickEvent(MixpanelEvents.HelpCenterOpened);
      window?.analytics?.page?.('help-center');
    } else {
      this.shouldDisplaySupport = false;
    }
  }

  @action
  trackEvent(name: keyof EventsType['help_center'], eventData?: Record<string, string>) {
    let currentAccount = this.args.account;
    let { currentUser } = currentAccount;

    let data = {
      account_id: currentAccount.id,
      requester_name: currentUser.fullName,
      requester_email: currentUser.email,
      requester_user_id: currentUser.id,
      ...eventData,
    };
    this.analytics.track('help_center', name, { data });
  }

  @action
  handleSupportOpen() {
    if (this.features.isEnabled('inline_support')) {
      this.shouldDisplaySupport = true;
    } else {
      this.setHelpType(HelpType.Support);
    }
    this.trackClickEvent(MixpanelEvents.HelpRequestStarted);

    window?.analytics?.page?.('help-center/support');
  }

  @action
  handleSupportClose() {
    this.shouldDisplaySupport = false;
  }

  @action
  backToHelpCenter() {
    this.shouldDisplaySupport = true;
  }

  @action
  trackClickEvent(event: MixpanelEvents) {
    this.trackEvent('menu_item_clicked', {
      button_clicked: event as string,
    });
  }

  @action
  trackDocsSearched(results: any[], time: number) {
    let helpCenterPerformanceIndicatorObjective = 1000; // ms
    let randomSample = Math.random() <= 0.1; // % only send up 10% of searches
    if (randomSample || time >= helpCenterPerformanceIndicatorObjective) {
      this.trackEvent('docs_searched_duration', { duration: String(time) });
    }
  }

  @action
  trackDocsSearchInputEvent() {
    if (this.hasTrackedDocsSearchInput) {
      return;
    }
    this.hasTrackedDocsSearchInput = true;
    this.trackEvent('docs_search_input');
  }

  @action
  trackDocsSearchResultsClickEvent(title: string) {
    this.trackEvent('docs_search_results_clicked', { docs_title: title });
  }

  @action
  handleUserInput(hasUserInput: boolean) {
    this.hasUserInput = hasUserInput;
  }

  @action
  handleCancel() {
    this.analytics.track('inline_support', 'inline_support_cancel_clicked');
    if (this.hasUserInput) {
      this.isCancelModalVisible = true;
    } else {
      this.toggleSidebar();
    }
  }

  @action
  handleCloseCancelModal() {
    this.analytics.track('inline_support', 'inline_support_cancel_dismissed');
    this.isCancelModalVisible = false;
  }

  @action
  handleDiscardSupportRequest() {
    this.analytics.track('inline_support', 'inline_support_cancel_confirmed');
    this.isCancelModalVisible = false;
    this.shouldDisplaySupport = false;
    this.hasUserInput = false;
  }
  @action
  handleAiBannerClick() {
    return this.trackEvent('ai_powered_search_banner_clicked');
  }
}
